// src/layout/MainLayout.tsx

import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { apiGet } from "../api/client";
import { AuthMe } from "../types/auth";
import { A } from "../styles/appleClasses";
import ScrollToTop from "../components/ScrollToTop";
import { usePostHog } from "@posthog/react";

export default function MainLayout() {
  const [user, setUser] = useState<AuthMe | null>(null);
  const location = useLocation();
  const { company_id } = useParams();
  const posthog = usePostHog();

  // Hide the sidebar on onboarding / welcome screens
  const hideSidebar =
    location.pathname === "/welcome" ||
    location.pathname.startsWith("/welcome");

  useEffect(() => {
    apiGet<AuthMe>("/auth/me")
      .then((data) => {
        setUser(data);
        // optional sanity logging during dev:
        console.log("MainLayout /auth/me ->", data);
      })
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!posthog) return;
    if (!user?.id) return;

    posthog.identify(`user:${user.id}`, {
      email: user.email || "",
      name: user.full_name || "",
      company_id: company_id || String(user.company_id || ""),
      company_name: user.company_name || "",
      is_company_admin: user.is_company_admin,
      is_ricebook_admin: user.is_ricebook_admin,
      account_tier: user.account_tier || "",
      is_user_pro: user.is_user_pro,
      email_verified: user.email_verified,
      app_env: process.env.REACT_APP_ENV || process.env.NODE_ENV || "development",
    });
  }, [posthog, user, company_id]);

  return (
    <div className={`flex min-h-screen bg-gray-50 ${A.body}`}>
      {!hideSidebar && <Sidebar user={user ?? undefined} />}

      <div className="flex flex-col flex-1 min-w-0">
        {/* ensures new pages start at top */}
        <ScrollToTop />

        <main
          className={[
            "flex-1",
            "px-4 sm:px-6 lg:px-8",
            hideSidebar ? "pt-8 pb-10" : "pt-16 md:pt-8 pb-10",
          ].join(" ")}
        >
          <div className="w-full max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}